import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';

const plans = [
  {
    id: 'free',
    name: 'Free',
    price: '0',
    interval: 'month',
    features: [
      '100 image generations per month',
      'High resolution (1024x1024)',
      'Standard processing speed'
    ],
    priceId: null
  },
  {
    id: 'basic',
    name: 'Basic',
    price: '4.99',
    interval: 'month',
    features: [
      '500 image generations per month',
      'High resolution (1024x1024)',
      'Email support',
    ],
    priceId: process.env.REACT_APP_STRIPE_PRICE_BASIC
  },
  {
    id: 'pro',
    name: 'Professional',
    price: '9.99',
    interval: 'month',
    features: [
      '5000 image generations per month',
      'High resolution (1024x1024)',
      'Priority support'
  
    ],
    popular: true,
    priceId: process.env.REACT_APP_STRIPE_PRICE_PRO
  }
];

function Pricing() {
  const navigate = useNavigate();
  const { token, isAuthenticated } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated) {
      fetchSubscription();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, token]);

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API_URL}/subscription`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setSubscription(response.data.subscription);
    } catch (err) {
      console.error('Error fetching subscription:', err);
    } finally {
      setLoading(false);
    }
  };

  const API_URL = process.env.REACT_APP_API_URL;

  const handleSubscribe = async (priceId) => {
    if (priceId === null) {
      // For free plan, just redirect to image generator if logged in
      if (!isAuthenticated) {
        window.location.href = '/signin?redirect=/image-generator';
        return;
      }
      window.location.href = '/image-generator';
      return;
    }

    if (!isAuthenticated) {
      window.location.href = '/signin?redirect=/pricing';
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(
        `${API_URL}/create-checkout-session`,
        { priceId },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Failed to start checkout process. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getButtonText = (plan) => {
    if (loading) return <CircularProgress size={24} sx={{ color: '#000' }} />;
    if (plan.id === 'free') return 'Start Free';
    return 'Subscribe Now';
  };

  const getButtonStyles = (plan) => ({
    mt: 4,
    bgcolor: '#00ffea',
    color: '#000',
    border: 'none',
    fontWeight: 600,
    '&:hover': {
      bgcolor: '#00b8ff',
      border: 'none',
      color: '#000'
    },
    '&.Mui-disabled': {
      background: 'linear-gradient(45deg, rgba(0, 255, 234, 0.3) 30%, rgba(0, 184, 255, 0.3) 90%)',
      color: '#000',
      opacity: 0.9,
      border: '1px solid rgba(0, 255, 234, 0.5)'
    },
    transition: 'all 0.2s ease-in-out',
    ...(plan.id === 'free' && {
      bgcolor: '#00ffea',
      '&:hover': {
        bgcolor: '#00b8ff'
      }
    })
  });

  const renderPlanButton = (plan) => {
    if (!subscription || subscription.status === 'cancelled') {
      return (
        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={() => handleSubscribe(plan.priceId)}
          sx={getButtonStyles(plan)}
        >
          {getButtonText(plan)}
        </Button>
      );
    }
    
    // If user has an active subscription
    if (subscription.status === 'active') {
      if (subscription.stripePriceId === plan.priceId) {
        return (
          <Button
            variant="contained"
            size="large"
            fullWidth
            disabled
            sx={getButtonStyles(plan)}
          >
            Current Plan
          </Button>
        );
      } else {
        return (
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={() => navigate('/profile')}
            sx={getButtonStyles(plan)}
          >
            Change Plan in Profile
          </Button>
        );
      }
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      {subscription?.status === 'active' && (
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="primary" gutterBottom>
            You're Currently Subscribed
          </Typography>
          <Typography variant="body1" color="text.secondary">
            To change your plan, please visit your profile page
          </Typography>
        </Box>
      )}

      <Box textAlign="center" mb={8}>
        <Typography
          variant="h2"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 700,
            color: '#00ffea'
          }}
        >
          Choose Your Plan
        </Typography>
        <Typography
          variant="h5"
          color="text.secondary"
          sx={{ maxWidth: '800px', mx: 'auto', mb: 4 }}
        >
          Get access to powerful AI image generation capabilities with our flexible pricing plans
        </Typography>
      </Box>

      <Grid container spacing={4} justifyContent="center" sx={{ px: { xs: 2, md: 0 } }}>
        {plans.map((plan) => (
          <Grid item xs={12} md={4} key={plan.id}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                bgcolor: '#1a1b1e',
                border: plan.popular ? '2px solid #00ffea' : '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 4,
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-8px)'
                }
              }}
            >
              {plan.popular && (
                <Chip
                  label="Most Popular"
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    bgcolor: '#00ffea',
                    color: '#000',
                    fontWeight: 600
                  }}
                />
              )}
              <CardContent sx={{ flexGrow: 1, p: 4 }}>
                <Typography 
                  variant="h4"
                  component="div"
                  gutterBottom
                  sx={{
                    fontSize: '1.75rem',
                    fontWeight: 600,
                    letterSpacing: '0.5px'
                  }}
                >
                  {plan.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 4 }}>
                  <Typography 
                    variant="h4"
                    component="span"
                    sx={{
                      fontSize: '1.75rem',
                      fontWeight: 600
                    }}
                  >
                    ${plan.price}
                  </Typography>
                  <Typography 
                    variant="subtitle1"
                    color="text.secondary"
                    sx={{ 
                      ml: 1,
                      fontSize: '1rem'
                    }}
                  >
                    /month
                  </Typography>
                </Box>
                <List>
                  {plan.features.map((feature) => (
                    <ListItem key={feature} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckIcon sx={{ color: '#00ffea' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={feature}
                        primaryTypographyProps={{
                          sx: { color: 'rgba(255, 255, 255, 0.8)' }
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
                <CardActions sx={{ p: 3 }}>
                  {renderPlanButton(plan)}
                </CardActions>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
}

export default Pricing; 